/**
 * Transaction handlers for new BFF API spec
 * GET /bff/merchant/transactions
 */

import { http, HttpResponse } from 'msw'
import { getTransactions } from '../data/transactions'
import { processListRequest } from '../utils/filters'
import { applyDelay } from '../utils/delays'
import type { RefundItem } from '../../app/types/transactions'

/**
 * Match production timestamp format: "YYYY-MM-DD HH:mm:ss.ffffff"
 */
function formatCreatedAt(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  const iso = date.toISOString()
  const [datePart, timePart = ''] = iso.split('T')
  const [time = '', msPart = ''] = timePart.split('.')
  const ms = msPart.replace('Z', '')
  const micro = `${ms}000`.slice(0, 6)

  return `${datePart} ${time}.${micro}`
}

/**
 * Transform a transaction to the API response format
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function transformTransaction(transaction: any) {
  const { created_at, ...rest } = transaction ?? {}
  return {
    ...rest,
    created_at: created_at ? formatCreatedAt(created_at) : created_at
  }
}

const REFUNDABLE_STATUSES = new Set(['paid', 'confirmed', 'partially_refunded'])

export const transactionHandlers = [
  /**
   * POST /api/bff/merchant/transactions/charge/:chargeUuid/refund/:walletUuid/
   * Supports full and partial refunds
   */
  http.post('/api/bff/merchant/transactions/charge/:chargeUuid/refund/:walletUuid', async ({ params, request }) => {
    await applyDelay({ preset: 'slow' })

    const { chargeUuid, walletUuid } = params
    const transactions = getTransactions()
    const transaction = transactions.find(t => t.charge_uuid === chargeUuid)

    if (!transaction) {
      return HttpResponse.json(
        { code: 'errors.wallet.charge_not_available_refund' },
        { status: 404 }
      )
    }

    if (!REFUNDABLE_STATUSES.has(transaction.status)) {
      return HttpResponse.json(
        { code: 'errors.wallet.charge_not_available_refund' },
        { status: 404 }
      )
    }

    const url = new URL(request.url)
    const body = await request.json().catch(() => ({})) as { amount?: number }
    const currentRemaining = transaction.refund_data?.amount_remaining ?? transaction.amount
    const refundAmount = body.amount ?? currentRemaining

    // Simulate refund_error as HTTP 400 (matches production API behaviour).
    // Force via ?simulate=refund_error or randomly ~30% of attempts.
    const simulateRefundError = url.searchParams.get('simulate') === 'refund_error' || Math.random() < 0.3

    if (simulateRefundError) {
      return HttpResponse.json({
        charge_uuid: transaction.charge_uuid,
        wallet_uuid: walletUuid as string,
        status: 'refund_error',
        amount_refunded: 0,
        amount_remaining: currentRemaining,
        refunds: [{
          refund_uuid: crypto.randomUUID(),
          amount: refundAmount,
          status: 'refund_error',
          created: new Date().toISOString()
        }],
        message: [{
          code: 'charge_refund_error_waiting_retry',
          description: 'An error occurred while processing the refund with the provider. A new automatic retry will be attempted shortly.'
        }]
      }, { status: 400 })
    }

    const isPartial = refundAmount < currentRemaining

    const newRefundItem: RefundItem = {
      refund_uuid: crypto.randomUUID(),
      amount: refundAmount,
      status: 'refunded',
      created: new Date().toISOString()
    }

    const prevRefunds = transaction.refund_data?.refunds ?? []
    const totalRefunded = Number(((transaction.refund_data?.amount_refunded ?? 0) + refundAmount).toFixed(2))
    const amountRemaining = Number((transaction.amount - totalRefunded).toFixed(2))

    transaction.refund_data = {
      amount_refunded: totalRefunded,
      amount_remaining: amountRemaining,
      refunds: [newRefundItem, ...prevRefunds]
    }

    if (isPartial) {
      transaction.status = 'partially_refunded'
    } else {
      transaction.status = 'refunded'
      transaction.date_refunded = new Date().toISOString()
    }

    return HttpResponse.json({
      charge_uuid: transaction.charge_uuid,
      wallet_uuid: walletUuid as string,
      status: transaction.status,
      amount_refunded: totalRefunded,
      amount_remaining: amountRemaining,
      refunds: transaction.refund_data.refunds,
      message: []
    })
  }),

  /**
   * GET /api/bff/merchant/transactions
   * List transactions with filtering, sorting, and pagination
   * Implements filter=campo:operador:valor pattern
   * Use filter=charge_uuid:eq:{value} to get a single transaction
   */
  http.get('/api/bff/merchant/transactions', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    // Apply delay simulation
    await applyDelay({ searchParams, preset: 'normal' })

    // Get mock data
    const transactions = getTransactions()

    // Process list request with new filter system
    const result = processListRequest(transactions, searchParams, {
      defaultSort: 'created:desc',
      defaultLimit: 20
    })

    const responseData = result.data.map(transformTransaction)

    return HttpResponse.json({
      ...result,
      data: responseData
    })
  })
]
